"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { ArrowRight, FileText, Network, Users, Shield } from "lucide-react";

export default function HomePage() {
  return (
    <main className="relative min-h-[100dvh] flex flex-col bg-background overflow-hidden">
      {/* Ambient background — single soft radial, grid masked */}
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 w-[720px] h-[720px] bg-primary opacity-[0.05] blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(30_10%_50%_/_0.04)_1px,transparent_1px),linear-gradient(to_bottom,hsl(30_10%_50%_/_0.04)_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none [mask-image:radial-gradient(ellipse_at_center,black,transparent_75%)]" />

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 lg:px-10 h-16 border-b border-border/40">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm tracking-tight shadow-sm shadow-primary/20">
            A
          </div>
          <span className="font-semibold text-foreground tracking-tight">Arsenal Wiki</span>
        </div>
        <Link
          href="/login"
          className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1.5"
        >
          Iniciar sesión
          <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </header>

      {/* Hero */}
      <section className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 py-20 max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/60 border border-border/50 text-xs font-medium text-muted-foreground mb-7"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-success" />
          Base de conocimiento para equipos
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
          className="text-4xl sm:text-5xl md:text-6xl font-semibold text-foreground tracking-tight-hero leading-[1.05] mb-6 text-balance"
        >
          Documentación que tu equipo <br className="hidden sm:block" />
          <span className="text-primary">realmente usará</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="text-base md:text-lg text-muted-foreground max-w-xl leading-relaxed mb-10 text-pretty"
        >
          Editor colaborativo en tiempo real, vínculos bidireccionales entre
          documentos, permisos por equipo y búsqueda instantánea. Todo en un
          solo lugar.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link
            href="/login"
            className="group inline-flex items-center justify-center gap-2 h-11 px-6 bg-primary text-primary-foreground rounded-lg font-medium text-sm transition-all hover:bg-primary/90 active:scale-[0.98] shadow-sm shadow-primary/15"
          >
            Empezar
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
          <Link
            href="/workspace"
            className="inline-flex items-center justify-center gap-2 h-11 px-6 bg-background border border-border text-foreground rounded-lg font-medium text-sm transition-all hover:bg-secondary/60 active:scale-[0.98]"
          >
            Ver demo
          </Link>
        </motion.div>
      </section>

      {/* Feature row — 3 calm cards, no clichéd "rocket" icons */}
      <section className="relative z-10 px-6 pb-20 max-w-5xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.28, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-3 gap-3"
        >
          {[
            {
              icon: FileText,
              title: "Editor colaborativo",
              body: "Edición simultánea con sincronización en tiempo real y presencia de otros editores.",
            },
            {
              icon: Network,
              title: "Vínculos bidireccionales",
              body: "Conecta documentos con [[wiki-links]] y navega el grafo de conocimiento visualmente.",
            },
            {
              icon: Shield,
              title: "Permisos por equipo",
              body: "Control de acceso por organización, proyecto y documento. DENY tiene prioridad sobre ALLOW.",
            },
          ].map((f) => (
            <div
              key={f.title}
              className="p-5 rounded-xl bg-card/60 border border-border/40 backdrop-blur-sm"
            >
              <div className="w-9 h-9 rounded-lg bg-secondary/60 border border-border/40 flex items-center justify-center text-foreground mb-3.5">
                <f.icon className="w-4 h-4" strokeWidth={1.5} />
              </div>
              <h3 className="text-sm font-semibold text-foreground mb-1.5">
                {f.title}
              </h3>
              <p className="text-xs text-muted-foreground leading-relaxed text-pretty">
                {f.body}
              </p>
            </div>
          ))}
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-border/40 px-6 py-5 text-center">
        <p className="text-xs text-muted-foreground">
          Arsenal Wiki · Base de conocimiento empresarial
        </p>
      </footer>
    </main>
  );
}